import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import ApiService from '../../services/api';

// CV creation steps
const steps = ['Personal Information', 'Education', 'Experience', 'Skills & Languages', 'Additional Information'];

// Education level options
const educationLevels = [
  'High School',
  'Associate Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctorate',
  'Professional Certification',
  'Other'
];

// Employment type options
const employmentTypes = [
  'Full-time',
  'Part-time',
  'Contract',
  'Internship',
  'Freelance',
  'Volunteer'
];

// Language proficiency levels
const proficiencyLevels = [
  'Native',
  'Fluent',
  'Advanced',
  'Intermediate',
  'Basic'
];

const CreateCV = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileUpload, setFileUpload] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Form validation schema
  const validationSchema = Yup.object({
    // Personal Information
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    address: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().required('Zip code is required'),
    country: Yup.string().required('Country is required'),
    title: Yup.string().required('Professional title is required'),
    summary: Yup.string().required('Professional summary is required'),
    
    // Education, Experience, Skills, etc. are validated in their respective steps
  });

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      // Personal Information
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      title: '',
      summary: '',
      website: '',
      linkedin: '',
      github: '',
      
      // Education
      education: [],
      
      // Experience
      experience: [],
      
      // Skills & Languages
      skills: [],
      languages: [],
      
      // Additional Information
      certifications: [],
      projects: [],
      interests: [],
      references: [],
      
      // CV Document
      cvFileUrl: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        setError('');
        setSuccess('');
        
        // Upload CV file if provided
        let cvFileUrl = values.cvFileUrl;
        if (fileUpload) {
          const formData = new FormData();
          formData.append('file', fileUpload);
          formData.append('userId', currentUser._id);
          
          const uploadResponse = await ApiService.request('/upload/cv', {
            method: 'POST',
            body: formData,
            headers: {}, // Let browser set content-type for FormData
          });
          
          if (uploadResponse.success) {
            cvFileUrl = uploadResponse.fileUrl;
          } else {
            throw new Error(uploadResponse.message || 'File upload failed');
          }
        }
        
        // Save CV data via API
        const cvData = {
          ...values,
          cvFileUrl,
          userId: currentUser._id,
          updatedAt: new Date().toISOString()
        };
        
        const response = await ApiService.request('/cvs', {
          method: 'POST',
          body: JSON.stringify(cvData),
        });
        
        if (response.success) {
          setSuccess('Your CV has been saved successfully!');
        } else {
          throw new Error(response.message || 'Failed to save CV');
        }
      } catch (err) {
        console.error('Error saving CV:', err);
        setError('Failed to save your CV. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  });

  // Fetch existing CV data if available (after formik is initialized)
  useEffect(() => {
    const fetchCvData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const response = await ApiService.request(`/cvs/${currentUser._id}`);
        
        if (response.success && response.cv) {
          const data = response.cv;
          formik.setValues({
            ...formik.values,
            ...data
          });
        }
      } catch (err) {
        console.error('Error fetching CV data:', err);
        setError('Failed to load your CV data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCvData();
  }, [currentUser, formik]);


  // Handle file upload
  const handleFileChange = (event) => {
    if (event.target.files[0]) {
      setFileUpload(event.target.files[0]);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle adding education entry
  const handleAddEducation = () => {
    formik.setFieldValue('education', [
      ...formik.values.education,
      {
        institution: '',
        degree: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        description: '',
        location: ''
      }
    ]);
  };

  // Handle removing education entry
  const handleRemoveEducation = (index) => {
    const newEducation = [...formik.values.education];
    newEducation.splice(index, 1);
    formik.setFieldValue('education', newEducation);
  };

  // Handle adding experience entry
  const handleAddExperience = () => {
    formik.setFieldValue('experience', [
      ...formik.values.experience,
      {
        company: '',
        position: '',
        location: '',
        startDate: '',
        endDate: '',
        current: false,
        description: '',
        employmentType: ''
      }
    ]);
  };

  // Handle removing experience entry
  const handleRemoveExperience = (index) => {
    const newExperience = [...formik.values.experience];
    newExperience.splice(index, 1);
    formik.setFieldValue('experience', newExperience);
  };

  // Handle adding skill
  const handleAddSkill = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      const newSkill = event.target.value.trim();
      if (!formik.values.skills.includes(newSkill)) {
        formik.setFieldValue('skills', [...formik.values.skills, newSkill]);
      }
      event.target.value = '';
    }
  };

  // Handle removing skill
  const handleRemoveSkill = (skillToRemove) => {
    formik.setFieldValue(
      'skills',
      formik.values.skills.filter(skill => skill !== skillToRemove)
    );
  };

  // Handle adding language
  const handleAddLanguage = () => {
    formik.setFieldValue('languages', [
      ...formik.values.languages,
      {
        language: '',
        proficiency: ''
      }
    ]);
  };

  // Handle removing language
  const handleRemoveLanguage = (index) => {
    const newLanguages = [...formik.values.languages];
    newLanguages.splice(index, 1);
    formik.setFieldValue('languages', newLanguages);
  };

  // Handle adding certification
  const handleAddCertification = () => {
    formik.setFieldValue('certifications', [
      ...formik.values.certifications,
      {
        name: '',
        issuer: '',
        date: '',
        description: '',
        url: ''
      }
    ]);
  };

  // Handle removing certification
  const handleRemoveCertification = (index) => {
    const newCertifications = [...formik.values.certifications];
    newCertifications.splice(index, 1);
    formik.setFieldValue('certifications', newCertifications);
  };

  // Toggle preview dialog
  const handleTogglePreview = () => {
    setPreviewDialogOpen(!previewDialogOpen);
  };

  // Render form steps
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Personal Information
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="city"
                name="city"
                label="City"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="state"
                name="state"
                label="State/Province"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="zipCode"
                name="zipCode"
                label="Zip/Postal Code"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                helperText={formik.touched.zipCode && formik.errors.zipCode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="country"
                name="country"
                label="Country"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="title"
                name="title"
                label="Professional Title"
                placeholder="e.g., Senior Software Engineer"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="summary"
                name="summary"
                label="Professional Summary"
                multiline
                rows={4}
                value={formik.values.summary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.summary && Boolean(formik.errors.summary)}
                helperText={formik.touched.summary && formik.errors.summary}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="website"
                name="website"
                label="Website (Optional)"
                value={formik.values.website}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="linkedin"
                name="linkedin"
                label="LinkedIn (Optional)"
                value={formik.values.linkedin}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="github"
                name="github"
                label="GitHub (Optional)"
                value={formik.values.github}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        );
      
      case 1: // Education
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Education</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddEducation}
              >
                Add Education
              </Button>
            </Box>
            
            {formik.values.education.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                No education entries yet. Click "Add Education" to add your educational background.
              </Typography>
            ) : (
              formik.values.education.map((edu, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Education #{index + 1}</Typography>
                    <IconButton color="error" onClick={() => handleRemoveEducation(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Institution"
                        value={edu.institution}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].institution = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Degree</InputLabel>
                        <Select
                          value={edu.degree}
                          label="Degree"
                          onChange={(e) => {
                            const newEducation = [...formik.values.education];
                            newEducation[index].degree = e.target.value;
                            formik.setFieldValue('education', newEducation);
                          }}
                        >
                          {educationLevels.map((level) => (
                            <MenuItem key={level} value={level}>{level}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Field of Study"
                        value={edu.fieldOfStudy}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].fieldOfStudy = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="month"
                        value={edu.startDate}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].startDate = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date (or Expected)"
                        type="month"
                        value={edu.endDate}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].endDate = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={edu.location}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].location = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={edu.description}
                        onChange={(e) => {
                          const newEducation = [...formik.values.education];
                          newEducation[index].description = e.target.value;
                          formik.setFieldValue('education', newEducation);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )}
          </Box>
        );
      
      case 2: // Experience
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Work Experience</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddExperience}
              >
                Add Experience
              </Button>
            </Box>
            
            {formik.values.experience.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                No experience entries yet. Click "Add Experience" to add your work history.
              </Typography>
            ) : (
              formik.values.experience.map((exp, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Experience #{index + 1}</Typography>
                    <IconButton color="error" onClick={() => handleRemoveExperience(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company"
                        value={exp.company}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].company = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Position"
                        value={exp.position}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].position = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Employment Type</InputLabel>
                        <Select
                          value={exp.employmentType}
                          label="Employment Type"
                          onChange={(e) => {
                            const newExperience = [...formik.values.experience];
                            newExperience[index].employmentType = e.target.value;
                            formik.setFieldValue('experience', newExperience);
                          }}
                        >
                          {employmentTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={exp.location}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].location = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="month"
                        value={exp.startDate}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].startDate = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="month"
                        value={exp.endDate}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].endDate = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                        disabled={exp.current}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={exp.description}
                        onChange={(e) => {
                          const newExperience = [...formik.values.experience];
                          newExperience[index].description = e.target.value;
                          formik.setFieldValue('experience', newExperience);
                        }}
                        placeholder="Describe your responsibilities, achievements, and the technologies/tools you used."
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )}
          </Box>
        );
      
      case 3: // Skills & Languages
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Skills</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Add your technical and soft skills (press Enter after each skill)
                </Typography>
                
                <TextField
                  fullWidth
                  label="Add Skill"
                  placeholder="Type a skill and press Enter"
                  onKeyDown={handleAddSkill}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formik.values.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      onDelete={() => handleRemoveSkill(skill)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Languages</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddLanguage}
                    size="small"
                  >
                    Add Language
                  </Button>
                </Box>
                
                {formik.values.languages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No languages added yet. Click "Add Language" to add languages you speak.
                  </Typography>
                ) : (
                  formik.values.languages.map((lang, index) => (
                    <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Language"
                            value={lang.language}
                            onChange={(e) => {
                              const newLanguages = [...formik.values.languages];
                              newLanguages[index].language = e.target.value;
                              formik.setFieldValue('languages', newLanguages);
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <FormControl fullWidth>
                            <InputLabel>Proficiency</InputLabel>
                            <Select
                              value={lang.proficiency}
                              label="Proficiency"
                              onChange={(e) => {
                                const newLanguages = [...formik.values.languages];
                                newLanguages[index].proficiency = e.target.value;
                                formik.setFieldValue('languages', newLanguages);
                              }}
                            >
                              {proficiencyLevels.map((level) => (
                                <MenuItem key={level} value={level}>{level}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton color="error" onClick={() => handleRemoveLanguage(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
          </Grid>
        );
      
      case 4: // Additional Information
        return (
          <Grid container spacing={3}>
            {/* Certifications */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Certifications</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddCertification}
                    size="small"
                  >
                    Add Certification
                  </Button>
                </Box>
                
                {formik.values.certifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No certifications added yet.
                  </Typography>
                ) : (
                  formik.values.certifications.map((cert, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Certification #{index + 1}</Typography>
                        <IconButton size="small" color="error" onClick={() => handleRemoveCertification(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Certification Name"
                            value={cert.name}
                            onChange={(e) => {
                              const newCertifications = [...formik.values.certifications];
                              newCertifications[index].name = e.target.value;
                              formik.setFieldValue('certifications', newCertifications);
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Issuing Organization"
                            value={cert.issuer}
                            onChange={(e) => {
                              const newCertifications = [...formik.values.certifications];
                              newCertifications[index].issuer = e.target.value;
                              formik.setFieldValue('certifications', newCertifications);
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date"
                            type="month"
                            value={cert.date}
                            onChange={(e) => {
                              const newCertifications = [...formik.values.certifications];
                              newCertifications[index].date = e.target.value;
                              formik.setFieldValue('certifications', newCertifications);
                            }}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="URL (Optional)"
                            value={cert.url}
                            onChange={(e) => {
                              const newCertifications = [...formik.values.certifications];
                              newCertifications[index].url = e.target.value;
                              formik.setFieldValue('certifications', newCertifications);
                            }}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
            
            {/* CV Upload */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Upload CV Document</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Upload your existing CV document (PDF, DOC, or DOCX format)
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="cv-file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="cv-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Select File
                    </Button>
                  </label>
                  
                  {fileUpload && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="body2">
                        Selected: {fileUpload.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(fileUpload.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  )}
                  
                  {formik.values.cvFileUrl && !fileUpload && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="body2">
                        Current CV: <Link href={formik.values.cvFileUrl} target="_blank" rel="noopener">View</Link>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        );
      
      default:
        return 'Unknown step';
    }
  };

  // CV Preview Dialog
  const renderPreviewDialog = () => (
    <Dialog
      open={previewDialogOpen}
      onClose={handleTogglePreview}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>CV Preview</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <Typography variant="h4" gutterBottom>
            {formik.values.firstName} {formik.values.lastName}
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {formik.values.title}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Typography variant="body2">
              {formik.values.email}
            </Typography>
            <Typography variant="body2">
              {formik.values.phone}
            </Typography>
            <Typography variant="body2">
              {formik.values.city}, {formik.values.state}, {formik.values.country}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>Professional Summary</Typography>
          <Typography variant="body1" paragraph>
            {formik.values.summary}
          </Typography>
          
          {formik.values.experience.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>Experience</Typography>
              {formik.values.experience.map((exp, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {exp.position}
                  </Typography>
                  <Typography variant="subtitle2">
                    {exp.company} | {exp.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {exp.description}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          
          {formik.values.education.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>Education</Typography>
              {formik.values.education.map((edu, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {edu.degree} in {edu.fieldOfStudy}
                  </Typography>
                  <Typography variant="subtitle2">
                    {edu.institution} | {edu.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {edu.startDate} - {edu.endDate}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {edu.description}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          
          {formik.values.skills.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>Skills</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {formik.values.skills.map((skill, index) => (
                  <Chip key={index} label={skill} size="small" />
                ))}
              </Box>
            </>
          )}
          
          {formik.values.languages.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>Languages</Typography>
              <Box sx={{ mb: 2 }}>
                {formik.values.languages.map((lang, index) => (
                  <Typography key={index} variant="body2">
                    {lang.language}: {lang.proficiency}
                  </Typography>
                ))}
              </Box>
            </>
          )}
          
          {formik.values.certifications.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>Certifications</Typography>
              {formik.values.certifications.map((cert, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {cert.name} - {cert.issuer} ({cert.date})
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleTogglePreview}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create Your CV
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build a professional CV to showcase your skills and experience to potential employers
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}
        
        <Stepper
          activeStep={activeStep}
          sx={{ mb: 4, '& .MuiStepLabel-label': { whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' } }}
          orientation={isSmallScreen ? 'vertical' : 'horizontal'}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <form onSubmit={formik.handleSubmit}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {getStepContent(activeStep)}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {activeStep === steps.length - 1 && (
                    <Button
                      variant="outlined"
                      onClick={handleTogglePreview}
                      startIcon={<PreviewIcon />}
                    >
                      Preview
                    </Button>
                  )}
                  
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      Save CV
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </form>
      </Paper>
      
      {renderPreviewDialog()}
    </Container>
  );
};

export default CreateCV;